import * as React from "react";
import { toast } from "react-toastify";
import {
  useDispatch,
  useMappedState
} from "redux-react-hook";
import * as moment from "moment-timezone";

import {
  IGetProductFilters,
  useProductsApi,
  IProduct
} from "src/core/products/api/products.api";
import { setProduct } from "src/core/products/store/products.actions";
import type { IStore } from "src/core/shared/store";
import { setGlobalError } from "src/core/shared/store/global/global.actions";
import { useDrop } from "src/core/drops/hooks";
import { useCurrentUser } from "src/core/users/hooks";

const useProduct = (productId: number, filters?: IGetProductFilters | undefined) => {
  const [ isLoading, setLoading ] = React.useState(true);
  const dispatch = useDispatch();
  const [ getProductResponse, getProduct ] = useProductsApi("GET_PRODUCT");
  const [ saveProductResponse, saveProduct ] = useProductsApi("UPDATE_PRODUCT");
  const [ hasFetchedProduct, setHasFetchedProduct ] = React.useState(false);
  const [ { drop } ] = useDrop();
  const [ { currentRole } ] = useCurrentUser();

  const {
    product, currentOrder, installedModules
  } = useMappedState(({
    products, orders, global
  }: IStore) => ({
    product: products.product,
    currentOrder: orders.currentOrder,
    installedModules: global.settings ? global.settings.installedModules : []
  }));

  const reloadProduct = React.useCallback(() => {
    if (currentOrder?.mode === "edit" || currentRole?.type === "customer") {
      // do not get the product if not in edit mode!
      if (!drop?.id) {
        setHasFetchedProduct(false);

        return;
      }
    }

    getProduct({
      productId,
      filters: {
        ...filters,
        branchId: drop?.id,
        effectiveDate: installedModules.includes("price-change") && currentOrder?.mode === "edit" ?
          currentOrder?.data?.orderDeliveryDate || moment.utc().toDate() : undefined
      }
    });
  }, [
    currentOrder?.mode,
    currentOrder?.data?.orderDeliveryDate,
    currentRole?.type,
    getProduct,
    productId,
    filters,
    drop?.id,
    installedModules
  ]);

  const setCurrentProduct = (product: IProduct | null) => {
    dispatch(setProduct(product));
  };

  React.useEffect(() => {
    if (!hasFetchedProduct && (!product || product.id !== productId)) {
      if (product) {
        setCurrentProduct(null);
      }

      setHasFetchedProduct(true);
      reloadProduct();
    } else {
      setLoading(false);
    }
  }, [
    productId,
    product,
    hasFetchedProduct,
    reloadProduct,
    setCurrentProduct
  ]);

  React.useEffect(() => {
    if (saveProductResponse.data) {
      setCurrentProduct(saveProductResponse.data);
      toast.success("Product saved successfully");
    }

    if (saveProductResponse.error) {
      toast.error(`Could not save product - ${saveProductResponse.error.message}`);
    }
  }, [ saveProductResponse ]);

  React.useEffect(() => {
    if (getProductResponse.data) {
      setCurrentProduct(getProductResponse.data);
      setLoading(false);
    }

    if (getProductResponse.error) {
      setLoading(false);
      dispatch(setGlobalError(getProductResponse.error));
    }
  }, [ getProductResponse ]);

  return {
    isLoading,
    product,
    getProduct,
    reloadProduct,
    setCurrentProduct,
    saveProduct,
    isSavingProduct: saveProductResponse.pending || isLoading
  };
};

export default useProduct;
